(() => {
  const initRingSizeSelects = (scope = document) => {
    scope.querySelectorAll('[data-sgd-ring-size-select]').forEach((select) => {
      if (select.dataset.sgdReady === 'true') return;

      const productView = select.closest('.productView');
      const variantRadios = productView?.querySelector('variant-radios');
      const sourceFieldset = variantRadios?.querySelector(
        `.sgd-ring-size-radio-source[data-option-index="${select.dataset.optionIndex}"]`
      );

      if (!productView || !variantRadios || !sourceFieldset) return;

      const stickyCart = productView.querySelector('[data-sticky-add-to-cart]');
      const stickyButton = stickyCart?.querySelector('#product-sticky-add-to-cart');
      const stickyForm = stickyButton?.closest('form');
      const templateView = new URL(window.location.href).searchParams.get('view');

      const applyRequiredState = () => {
        const addButton = productView.querySelector('[name="add"]');
        if (!addButton) return;

        const sizeMissing = select.value === '';
        if (sizeMissing) {
          addButton.disabled = true;
          addButton.setAttribute('aria-disabled', 'true');
          addButton.textContent = 'Select Ring Size';
        }

        if (stickyCart && stickyButton) {
          stickyCart.dataset.sgdSizeRequired = String(sizeMissing);
          stickyButton.disabled = sizeMissing || addButton.disabled;
          stickyButton.setAttribute('aria-disabled', String(stickyButton.disabled));
          if (sizeMissing) stickyButton.textContent = 'Select Ring Size';
        }
      };

      select.dataset.sgdReady = 'true';
      applyRequiredState();

      stickyForm?.addEventListener('submit', (event) => {
        if (select.value !== '') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        select.scrollIntoView({ behavior: 'smooth', block: 'center' });
        select.focus();
        applyRequiredState();
      }, true);

      select.addEventListener('change', (event) => {
        event.stopPropagation();

        const sourceRadio = [...sourceFieldset.querySelectorAll('input[type="radio"]')]
          .find((radio) => radio.value === select.value);

        if (!sourceRadio) return;

        sourceRadio.checked = true;
        sourceRadio.dispatchEvent(new Event('change', { bubbles: true }));

        const addButton = productView.querySelector('[name="add"]');
        if (addButton && !addButton.disabled) {
          addButton.removeAttribute('aria-disabled');
        }

        requestAnimationFrame(applyRequiredState);

        const productUrl = new URL(window.location.href);
        if (templateView) productUrl.searchParams.set('view', templateView);
        else productUrl.searchParams.delete('view');
        window.history.replaceState({}, '', productUrl);
      });

      variantRadios.addEventListener('change', () => {
        requestAnimationFrame(applyRequiredState);
      });
    });
  };

  const initFaqTabs = (scope = document) => {
    scope.querySelectorAll('.sgd-reference-faq').forEach((faq) => {
      if (faq.dataset.sgdReady === 'true') return;

      const tabs = [...faq.querySelectorAll('[data-sgd-faq-topic]')];
      const questions = [...faq.querySelectorAll('[data-sgd-faq-category]')];
      if (!tabs.length || !questions.length) return;

      const setTopic = (topic) => {
        tabs.forEach((tab) => {
          tab.setAttribute('aria-pressed', String(tab.dataset.sgdFaqTopic === topic));
        });
        questions.forEach((question) => {
          const isVisible = question.dataset.sgdFaqCategory === topic;
          question.hidden = !isVisible;
          if (!isVisible) question.open = false;
        });
      };

      faq.dataset.sgdReady = 'true';
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => setTopic(tab.dataset.sgdFaqTopic));
      });
      setTopic(tabs[0].dataset.sgdFaqTopic);
    });
  };

  const initSizeHelp = (scope = document) => {
    scope.querySelectorAll('[data-sgd-size-help-open]').forEach((trigger) => {
      if (trigger.dataset.sgdHelpReady === 'true') return;
      const dialog = document.getElementById(trigger.dataset.sgdSizeHelpOpen);
      if (!dialog) return;
      trigger.dataset.sgdHelpReady = 'true';
      trigger.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-sgd-size-help-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => trigger.focus({ preventScroll: true }));
    });
  };

  const initGalleryVideos = (scope = document) => {
    scope.querySelectorAll('[data-sgd-gallery-video]').forEach((player) => {
      if (player.dataset.sgdReady === 'true') return;
      const video = player.querySelector('video');
      if (!video) return;
      player.dataset.sgdReady = 'true';
      const play = player.querySelector('[data-sgd-video-play]');
      const sound = player.querySelector('[data-sgd-video-sound]');
      const progress = player.querySelector('[data-sgd-video-progress]');
      video.controls = false;
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      let userPaused = false;
      player.querySelector('.sgd-gallery-video__controls').hidden = false;
      const sync = () => {
        player.classList.toggle('is-playing', !video.paused);
        player.classList.toggle('is-muted', video.muted);
        play.setAttribute('aria-label', video.paused ? 'Play product video' : 'Pause product video');
        sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        sound.setAttribute('aria-pressed', String(video.muted));
      };
      const toggle = () => {
        if (!video.paused) { userPaused = true; return video.pause(); }
        userPaused = false;
        video.play().catch(() => { video.controls = true; player.querySelector('.sgd-gallery-video__controls').hidden = true; });
      };
      play.addEventListener('click', toggle);
      video.addEventListener('click', toggle);
      sound.addEventListener('click', () => { video.muted = !video.muted; });
      ['play', 'pause', 'ended', 'volumechange'].forEach((event) => video.addEventListener(event, sync));
      video.addEventListener('timeupdate', () => { progress.value = Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration * 100 : 0; });
      progress.addEventListener('input', () => { if (Number.isFinite(video.duration)) video.currentTime = progress.value / 100 * video.duration; });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) video.pause();
          else if (!userPaused) video.play().catch(sync);
        });
        observer.observe(player);
      }
      sync();
      video.play().catch(sync);
    });
  };

  const init = (scope = document) => {
    initRingSizeSelects(scope);
    initFaqTabs(scope);
    initSizeHelp(scope);
    initGalleryVideos(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();


/* Keep mobile sticky variants in sync with the product form. */
(() => {
  const initStickyMobileOptions = (scope = document) => {
    scope.querySelectorAll('.sticky-product-mobile variant-sticky-radios').forEach((picker) => {
      if (picker.dataset.sgdMobileReady === 'true') return;

      const productView = picker.closest('.productView');
      const radios = [...picker.querySelectorAll('input.product-form__radio.mobile')];
      if (!productView || !radios.length) return;

      radios.forEach((radio) => {
        if (radio.getAttribute('value')) return;
        const optionValue = radio.nextElementSibling?.textContent.trim();
              if (optionValue) {
        radio.setAttribute('value', optionValue);
        radio.value = optionValue;
      }
      });

      picker.dataset.sgdMobileReady = 'true';
            picker.addEventListener('change', () => {
        picker.querySelectorAll('input.product-form__radio.mobile').forEach((optionRadio) => {
          if (optionRadio.value) return;
          const optionValue = optionRadio.nextElementSibling?.textContent.trim();
          if (optionValue) {
            optionRadio.setAttribute('value', optionValue);
            optionRadio.value = optionValue;
          }
        });
      }, true);

      picker.addEventListener('change', (event) => {
        const radio = event.target.closest('input.product-form__radio.mobile');
        if (!radio) return;

        const fieldset = radio.closest('[data-option-index]');
        const optionIndex = fieldset?.dataset.optionIndex;
        if (optionIndex == null) return;

        const requiredSizeSelect = productView.querySelector(
          '[data-sgd-ring-size-select][data-option-index="' + optionIndex + '"]'
        );
        if (!requiredSizeSelect || !radio.value) return;

        requiredSizeSelect.value = radio.value;
        requiredSizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
                requestAnimationFrame(() => {
          const stickyCart = picker.closest('[data-sticky-add-to-cart]');
          const variantInput = stickyCart?.querySelector('form input[name="id"]');
          const variantId = new URL(window.location.href).searchParams.get('variant') || radio.dataset.variantId;
          if (!variantInput || !variantId) return;
          variantInput.value = variantId;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStickyMobileOptions());
  } else {
    initStickyMobileOptions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initStickyMobileOptions(event.target);
  });
})();


/* Ensure the current mobile selection is present at the moment of cart submission. */
(() => {
  const syncStickyVariant = (target) => {
    const form = target?.closest?.('form[id^="product-form-sticky-"]');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    const variantId = new URL(window.location.href).searchParams.get('variant');
    if (!variantInput || !variantId) return;

    variantInput.value = variantId;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('#product-sticky-add-to-cart')) syncStickyVariant(event.target);
  }, true);

  document.addEventListener('submit', (event) => {
    syncStickyVariant(event.target);
  }, true);
})();
